import { State } from "..";

import { AbstractStore } from ".";

const MAX_FAVORITES = 200;

export interface FavoriteGif {
  /**
   * Stable identity used to dedupe the same GIF across different URLs.
   */
  key: string;

  /**
   * URL used to send this GIF in chat.
   */
  url: string;

  /**
   * Preferred preview URL for picker rendering.
   */
  preview_url: string;

  /**
   * Timestamp for ordering (newest first).
   */
  added_at: number;
}

export interface TypeGifFavorites {
  gifs: FavoriteGif[];
}

type FavoriteGifInput = Pick<FavoriteGif, "url" | "preview_url"> & {
  key?: string;
  filename?: string;
};

type LegacyFavoriteGifInput = Partial<FavoriteGif> & {
  previewUrl?: string;
  preview?: string;
  src?: string;
  addedAt?: number;
  filename?: string;
};

function normalizeFavoritePart(value: string) {
  return decodeURIComponent(value).trim().toLowerCase();
}

function buildFavoriteKey(entry: Pick<FavoriteGifInput, "url" | "key" | "filename">) {
  if (entry.key) {
    const normalized = normalizeFavoritePart(entry.key);
    return normalized.includes(":") ? normalized : `file:${normalized}`;
  }

  if (entry.filename) {
    return `file:${normalizeFavoritePart(entry.filename)}`;
  }

  try {
    const parsed = new URL(entry.url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    const last = segments.at(-1);
    const fallback = `${parsed.host}${parsed.pathname}${parsed.search}`;

    if (!last) {
      return `url:${normalizeFavoritePart(fallback)}`;
    }

    if (last.toLowerCase() === "original" && segments.length > 1) {
      return `file:${normalizeFavoritePart(segments.at(-2)!)}`
    }

    return `file:${normalizeFavoritePart(last)}`;
  } catch {
    return `url:${normalizeFavoritePart(entry.url)}`;
  }
}

function normalizeFavoriteEntry(entry: string | LegacyFavoriteGifInput) {
  if (typeof entry === "string") {
    return {
      url: entry,
      preview_url: entry,
      added_at: Date.now(),
    };
  }

  if (!entry || typeof entry !== "object") {
    return;
  }

  const url =
    typeof entry.url === "string"
      ? entry.url
      : typeof entry.src === "string"
        ? entry.src
        : undefined;

  const preview_url =
    typeof entry.preview_url === "string"
      ? entry.preview_url
      : typeof entry.previewUrl === "string"
        ? entry.previewUrl
        : typeof entry.preview === "string"
          ? entry.preview
          : url;

  if (!url || !preview_url) {
    return;
  }

  return {
    url,
    preview_url,
    key: typeof entry.key === "string" ? entry.key : undefined,
    filename: typeof entry.filename === "string" ? entry.filename : undefined,
    added_at:
      typeof entry.added_at === "number"
        ? entry.added_at
        : typeof entry.addedAt === "number"
          ? entry.addedAt
          : Date.now(),
  };
}

/**
 * Stores GIF favorites that are synced across user devices.
 */
export class GifFavorites extends AbstractStore<"gif_favorites", TypeGifFavorites> {
  constructor(state: State) {
    super(state, "gif_favorites");
  }

  hydrate(): void {
    /** nothing needs to be done */
  }

  default(): TypeGifFavorites {
    return {
      gifs: [],
    };
  }

  clean(input: Partial<TypeGifFavorites>): TypeGifFavorites {
    const out: TypeGifFavorites = this.default();

    if (Array.isArray(input.gifs)) {
      for (const entry of input.gifs) {
        const normalized = normalizeFavoriteEntry(entry as string | LegacyFavoriteGifInput);
        if (!normalized) continue;

        out.gifs.push({
          key: buildFavoriteKey(normalized),
          url: normalized.url,
          preview_url: normalized.preview_url,
          added_at: normalized.added_at,
        });
      }
    }

    // Keep newest first, unique by canonical GIF identity and bounded for sync payload size.
    const unique = new Map<string, FavoriteGif>();
    out.gifs
      .sort((a, b) => b.added_at - a.added_at)
      .forEach((gif) => {
        if (!unique.has(gif.key) && unique.size < MAX_FAVORITES) {
          unique.set(gif.key, gif);
        }
      });

    out.gifs = [...unique.values()];
    return out;
  }

  listFavorites() {
    return this.get().gifs;
  }

  isFavorite(entry: string | FavoriteGifInput) {
    const key =
      typeof entry === "string"
        ? buildFavoriteKey({ url: entry })
        : buildFavoriteKey(entry);

    return this.get().gifs.some((gif) => gif.key === key);
  }

  toggleFavorite(entry: FavoriteGifInput) {
    if (this.isFavorite(entry)) {
      this.removeFavorite(entry);
      return;
    }

    this.addFavorite(entry);
  }

  addFavorite(entry: FavoriteGifInput) {
    const now = Date.now();
    const key = buildFavoriteKey(entry);
    const next = [
      {
        key,
        url: entry.url,
        preview_url: entry.preview_url,
        added_at: now,
      },
      ...this.get().gifs.filter((gif) => gif.key !== key),
    ].slice(0, MAX_FAVORITES);

    this.set("gifs", next);
  }

  removeFavorite(entry: string | FavoriteGifInput) {
    const key =
      typeof entry === "string"
        ? buildFavoriteKey({ url: entry })
        : buildFavoriteKey(entry);

    this.set(
      "gifs",
      this.get().gifs.filter((gif) => gif.key !== key),
    );
  }
}
